import React, { useState } from "react";
import { HiOutlineTrash } from "react-icons/hi";

import { PaziaButton } from "components/atoms";
import { Switch } from "components/atoms/PaziaSwitch/Switch";
import { FieldGroup } from "./columnGroups";
import { buildFieldSetFromSelection, deleteFieldSet, FieldSet, loadSavedSets, saveFieldSet } from "./savedViews";

interface ColumnsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  groups: FieldGroup[];
  visibleColCount: number;
  selectedFields: string[];
  onToggleField: (key: string) => void;
  onToggleGroup: (fields: FieldGroup["fields"], value: boolean) => void;
  onReset: () => void;
  onLoadFieldSet: (set: FieldSet) => void;
  onSavedSetsChange?: (sets: FieldSet[]) => void;
  onFieldSetSaved?: (name: string) => void;
}

export const ColumnsDrawer = ({
  isOpen,
  onClose,
  groups,
  visibleColCount,
  selectedFields,
  onToggleField,
  onToggleGroup,
  onReset,
  onLoadFieldSet,
  onSavedSetsChange,
  onFieldSetSaved,
}: ColumnsDrawerProps) => {
  const [savedSets, setSavedSets] = useState<FieldSet[]>(loadSavedSets());
  const [newSetName, setNewSetName] = useState("");

  if (!isOpen) return null;

  const refreshSavedSets = () => {
    const sets = loadSavedSets();
    setSavedSets(sets);
    onSavedSetsChange?.(sets);
  };

  const handleSave = () => {
    if (!newSetName) return;
    saveFieldSet(buildFieldSetFromSelection(newSetName, selectedFields));
    refreshSavedSets();
    onFieldSetSaved?.(newSetName);
    setNewSetName("");
  };

  const handleDelete = (name: string) => {
    deleteFieldSet(name);
    refreshSavedSets();
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-slate-900/40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-[71] flex w-96 flex-col bg-white shadow-2xl dark:bg-slate-800">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700">
          <div>
            <div className="text-base font-extrabold text-slate-900 dark:text-slate-100">Kolumny i widoki</div>
            <div className="mt-0.5 text-xs text-slate-400">{visibleColCount} kolumn w tabeli</div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Zamknij"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-5 rounded-xl border border-slate-100 bg-slate-50 p-3.5 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Zapisane widoki</div>
            {savedSets.length > 0 ? (
              <div className="mb-3 space-y-0.5">
                {savedSets.map((set) => (
                  <div
                    key={set.name}
                    className="group flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-white dark:hover:bg-slate-800"
                  >
                    <button
                      onClick={() => onLoadFieldSet(set)}
                      className="flex-1 truncate text-left text-sm font-semibold text-slate-700 dark:text-slate-200"
                    >
                      {set.name}
                    </button>
                    <button
                      onClick={() => handleDelete(set.name)}
                      title="Usuń widok"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-300 hover:bg-rose-50 hover:text-rose-600 group-hover:text-slate-400"
                    >
                      <HiOutlineTrash className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mb-3 text-xs text-slate-400">Brak zapisanych widoków.</div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                placeholder="Nazwa nowego widoku…"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              />
              <PaziaButton type="secondary" btnType="button" onClick={handleSave} disabled={!newSetName}>
                Zapisz
              </PaziaButton>
            </div>
          </div>

          {groups.map((group) => {
            const checkedCount = group.fields.filter((f) => selectedFields.includes(f.key)).length;
            const allChecked = checkedCount === group.fields.length && group.fields.length > 0;
            return (
              <div key={group.name} className="mb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: group.dot }} />
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{group.name}</span>
                  </div>
                  <button
                    onClick={() => onToggleGroup(group.fields, !allChecked)}
                    className="rounded px-1.5 py-0.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    {allChecked ? "Wyczyść" : "Zaznacz wszystkie"}
                  </button>
                </div>
                {group.fields.map((field) => {
                  const checked = selectedFields.includes(field.key);
                  return (
                    <div
                      key={field.key}
                      className="flex items-center justify-between border-b border-slate-100 py-2 dark:border-slate-700"
                    >
                      <span
                        onClick={() => onToggleField(field.key)}
                        className={
                          "cursor-pointer text-sm " +
                          (checked ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-400")
                        }
                      >
                        {field.label}
                      </span>
                      <Switch
                        name={field.key}
                        checked={checked}
                        onChange={() => onToggleField(field.key)}
                        onBlur={() => {}}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2.5 border-t border-slate-100 px-5 py-3.5 dark:border-slate-700">
          <PaziaButton type="tertiary" btnType="button" className="flex-1" onClick={onReset}>
            Resetuj
          </PaziaButton>
          <PaziaButton type="primary" btnType="button" className="flex-[2]" onClick={onClose}>
            Zastosuj
          </PaziaButton>
        </div>
      </div>
    </>
  );
};
